import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { Pagination, SurveysList, UserContentSurveyList } from '@core/entities';
import { FilterQuery } from '@core/types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GetUserId } from '@core/utils';

@Injectable({
  providedIn: 'root',
})
export class SurveyService {
  private readonly _http = inject(HttpClient);

  private readonly _apiUrl = environment.apiUrl;
  private readonly _userId = GetUserId();

  get(id: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/getSurveyById/${id}`).pipe(map((response) => response.result[0]));
  }

  create(formData: FormData): Observable<any> {
    return this._http.post<any>(`${this._apiUrl}/createSurvey`, formData);
  }

  createSurveyVoting(formData: FormData): Observable<any> {
    return this._http.post<any>(`${this._apiUrl}/surveyVotes`, formData);
  }

  update(id: number, formData: FormData): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/updateSurvey/${id}`, formData);
  }

  delete(id: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/deleteSurvey/${id}`);
  }

  getCompletedSurveyByUserId(userId: number, pagination: Partial<Pagination>): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/getCompletedSurveyUser/user/${userId}/${pagination.page}/${pagination.pageSize}`).pipe(map((response) => response.result));
  }

  getSurveyResultsById(id: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/getResultSurveyById/survey/${id}`).pipe(map((response) => response.result));
  }

  getSurveyResultsByUserId(id: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/surveyResultDetails/survey/${id}/userId/${userId}`).pipe(map((response) => response.result));
  }

  getContentSurveys(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<UserContentSurveyList> {
    return this._http.get<any>(`${this._apiUrl}/getOwnerSurvey/${pagination.page}/${pagination.pageSize}`, { params: queryParams });
  }

  getActiveSurveys(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<SurveysList> {
    return this._http.get<any>(`${this._apiUrl}/getActivePollSurvey/${pagination.page}/${pagination.pageSize}`, { params: queryParams }).pipe(map((response) => response.result));
  }

  getActivePollSurveyUserById(userId: number, pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/getActivePollSurveyUser/user/${userId}/${pagination.page}/${pagination.pageSize}`, { params: queryParams }).pipe(map((response) => response.result));
  }

  getCompletedSurveys(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<SurveysList> {
    return this._http.get<any>(`${this._apiUrl}/getCompletedSurvey/${pagination.page}/${pagination.pageSize}`, { params: queryParams }).pipe(map((response) => response.result));
  }

  getCastedVote(surveyId: number, answerId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/surveyResult/survey/${surveyId}/answerId/${answerId}`).pipe(map((response) => response.result));
  }

  getMySurveys(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/getMySurveyVotes/user/${this._userId}/${pagination.page}/${pagination.pageSize}`, { params: queryParams }).pipe(map((response) => response.result));
  }

  activeCloseStatusUpdate(id: number): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/closeSurvey/survey/${id}`, {});
  }
}
